import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldLabel } from '@/components/ui/field';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useFetch } from '@/hooks/useFecth';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import { useClubConfirm } from '../providers/ClubConfirmProvider';
import { formatBirthday, formatDate } from '../utils/subscriberDates';
import CancelSubscriptionDialog from './CancelSubscriptionDialog';

const PLAN_VISIBILITY_LABELS = {
    PUBLIC: 'Público',
    PRIVATE: 'Privado',
    CUSTOM: 'Customizado',
};

function formatPlanPrice(value) {
    return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
}

function planYearlyPrice(plan) {
    const n = Number(plan?.yearlyPrice);
    return !plan?.isFree && Number.isFinite(n) && n > 0 ? n : null;
}

const CYCLE_LABELS = { MONTHLY: 'Mensal', YEARLY: 'Anual' };

function normalizeCycle(value) {
    return String(value || '').toUpperCase() === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
}

/** Ciclos vendidos pelo plano (sem `cycles`: mensal + anual quando houver valor anual). */
function planCycles(plan) {
    if (!plan || plan.isFree) return ['MONTHLY'];
    const yearly = planYearlyPrice(plan);
    const raw = Array.isArray(plan.cycles) && plan.cycles.length ? plan.cycles : ['MONTHLY', ...(yearly ? ['YEARLY'] : [])];
    const list = ['MONTHLY', 'YEARLY'].filter(
        cycle => raw.map(v => String(v).toUpperCase()).includes(cycle) && (cycle !== 'YEARLY' || yearly),
    );
    return list.length ? list : ['MONTHLY'];
}

/** Ciclos que o gestor pode escolher: os vendidos + o atual quando o plano é o mesmo do assinante. */
function selectableCycles(plan, { currentCycle, samePlan }) {
    const offered = planCycles(plan);
    if (!samePlan || !currentCycle || offered.includes(currentCycle)) return offered;
    if (currentCycle === 'YEARLY' && !planYearlyPrice(plan)) return offered;
    return ['MONTHLY', 'YEARLY'].filter(cycle => cycle === currentCycle || offered.includes(cycle));
}

function formatPlanPrices(plan) {
    const cycles = planCycles(plan);
    const yearly = planYearlyPrice(plan);
    const monthly = `${formatPlanPrice(plan?.price)}/mês`;
    if (cycles.length === 2) return `${monthly} ou ${formatPlanPrice(yearly)}/ano`;
    if (cycles[0] === 'YEARLY') return `${formatPlanPrice(yearly)}/ano`;
    return plan?.isFree ? formatPlanPrice(plan?.price) : monthly;
}

/** Rótulo compacto para o Select (lista + valor selecionado). */
function formatPlanOptionLabel(plan, { showPrice = true, cycle } = {}) {
    if (!plan) return 'Selecione';
    const parts = [plan.name || `Plano #${plan.id}`];
    if (plan.sku) parts.push(`SKU ${plan.sku}`);
    if (showPrice) {
        if (cycle && normalizeCycle(cycle) === 'YEARLY' && planYearlyPrice(plan)) {
            parts.push(`${formatPlanPrice(planYearlyPrice(plan))}/ano`);
        } else if (cycle) {
            parts.push(`${formatPlanPrice(plan.price)}/mês`);
        } else {
            parts.push(formatPlanPrices(plan));
        }
    }
    if (plan.isTrial) {
        parts.push(
            plan.trialPayment === false
                ? `Teste ${plan.trialDays || ''}d sem cobrança`
                : `Teste ${plan.trialDays || ''}d`,
        );
    }
    if (plan.isFree) parts.push('Gratuito');
    if (plan.status === false) parts.push('Inativo');
    return parts.join(' · ');
}

function planKitLabels(plan) {
    if (!Array.isArray(plan?.Kit)) return [];
    return plan.Kit.map(item => item?.Resale?.name || item?.Product?.name || item?.name)
        .filter(Boolean);
}

/** Detalhe completo do plano selecionado na tab Assinatura. */
function PlanDetailsPreview({ plan, showPrice = true }) {
    if (!plan) return null;

    const kit = planKitLabels(plan);
    const visibility = PLAN_VISIBILITY_LABELS[plan.visibility] || plan.visibility || '—';
    const dependents =
        plan.numberDependents ?? plan.numberLifes ?? null;

    return (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="font-medium leading-tight">{plan.name}</p>
                    {plan.sku ? (
                        <p className="mt-0.5 text-xs text-muted-foreground">SKU: {plan.sku}</p>
                    ) : null}
                </div>
                {showPrice ? (
                    <div className="shrink-0 text-right">
                        <p className="font-semibold tabular-nums">
                            {formatPlanPrice(plan.price)}
                            {!plan.isFree ? <span className="text-xs font-normal text-muted-foreground">/mês</span> : null}
                        </p>
                        {planYearlyPrice(plan) ? (
                            <p className="text-xs tabular-nums text-muted-foreground">
                                {formatPlanPrice(planYearlyPrice(plan))}/ano
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div className="mt-2 flex flex-wrap gap-1">
                <Badge variant="outline">{visibility}</Badge>
                <Badge variant={plan.status === false ? 'destructive' : 'secondary'}>
                    {plan.status === false ? 'Inativo' : 'Ativo'}
                </Badge>
                {plan.isFree ? <Badge variant="secondary">Gratuito</Badge> : null}
                {plan.isTrial ? (
                    <Badge variant="secondary">
                        {plan.trialPayment === false
                            ? `Teste ${plan.trialDays || ''}d sem cobrança`
                            : `Teste ${plan.trialDays || ''}d`}
                    </Badge>
                ) : null}
                {dependents != null ? (
                    <Badge variant="outline">
                        {Number(dependents)} dependente{Number(dependents) === 1 ? '' : 's'}
                    </Badge>
                ) : null}
            </div>

            {plan.description ? (
                <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{plan.description}</p>
            ) : null}

            {kit.length ? (
                <div className="mt-2">
                    <p className="text-xs font-medium text-muted-foreground">Benefícios do kit</p>
                    <ul className="mt-1 list-inside list-disc text-muted-foreground">
                        {kit.map(label => (
                            <li key={label}>{label}</li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

const CARD_REPLACEMENT_BILLING_ITEMS = { BOLETO: 'Boleto' };

function resolveMemberContext(initialData, club) {
    const subscription = initialData?.subscription;
    const isReseller = Boolean(club?.functions?.includes('reseller'));
    const hasBenefits = Boolean(club?.functions?.includes('benefit'));
    const isInstallmentMember =
        String(subscription?.billingModel || '').toUpperCase() === 'INSTALLMENT' ||
        Boolean(subscription?.installmentExternalId);
    const memberHasAsaas = Boolean(normalizeAsaasSubscriptionId(subscription?.subscriptionId)) || isInstallmentMember;
    const memberBillingType = String(subscription?.billingType || '').toUpperCase();
    /** Pacote Dinheiro: MONEY ou legado local sem Asaas com plano. */
    const isMoneyMember =
        !memberHasAsaas &&
        (memberBillingType === 'MONEY' || Boolean(Number(subscription?.planId)) || Boolean(subscription?.planData));
    const subscriptionStatus = String(subscription?.status || '').toUpperCase();
    /** Cobrança online encerrada (cancelada, estornada, fim do parcelado) pode virar pacote em dinheiro. */
    const memberHasActiveAsaas = (() => {
        if (!memberHasAsaas) return false;
        if (subscription?.scheduledPlanId) return true;
        if (isInstallmentMember) return !['CANCELLED', 'CANCELED', 'INACTIVE', 'EXPIRED'].includes(subscriptionStatus);
        return !['CANCELLED', 'CANCELED', 'INACTIVE'].includes(subscriptionStatus) && subscription?.cancelReason !== 'PLAN_CHANGE';
    })();

    return {
        isDependent: Boolean(initialData?.dependentBy),
        isReseller,
        hasBenefits,
        showPlanUi: isReseller || hasBenefits,
        clubHasAsaas: Boolean(club?.hasAsaasKey),
        memberHasAsaas,
        isMoneyMember,
        isInstallmentMember,
        memberHasActiveAsaas,
        isActiveInstallmentMember: isInstallmentMember && memberHasActiveAsaas,
        /** Sem cobrança Asaas em vigor (nunca teve, ou já cancelou/encerrou): atribui pelo pacote em dinheiro. */
        useMoneyChannel: isReseller && !memberHasActiveAsaas,
        assignViaMoney: isReseller && !memberHasActiveAsaas,
        subscriptionStatus,
        isSubscriptionCancelled: subscriptionStatus === 'CANCELLED',
        isSubscriptionInactive: subscriptionStatus === 'INACTIVE',
        currentCycle: normalizeCycle(subscription?.cycle),
        currentPlanId: subscription?.planId ? String(subscription.planId) : '',
    };
}

function buildFallbackPlan(subscription) {
    if (!subscription?.planData && !subscription?.Plan) return null;
    return {
        id: subscription.planId,
        name: subscription.planData?.name || subscription.Plan?.name || `Plano #${subscription.planId}`,
        sku: subscription.planData?.sku || subscription.Plan?.sku,
        price: subscription.planData?.price ?? subscription.Plan?.price ?? subscription.value,
        yearlyPrice: subscription.planData?.yearlyPrice ?? subscription.Plan?.yearlyPrice,
        cycles: subscription.Plan?.cycles ?? subscription.planData?.cycles,
        description: subscription.planData?.description || subscription.Plan?.description,
        visibility: subscription.planData?.visibility || subscription.Plan?.visibility,
        status: subscription.Plan?.status,
        isFree: subscription.planData?.isFree || subscription.Plan?.isFree,
        isTrial: subscription.planData?.isTrial || subscription.Plan?.isTrial,
        numberDependents: subscription.planData?.numberDependents ?? subscription.Plan?.numberDependents,
        numberLifes: subscription.planData?.numberLifes ?? subscription.Plan?.numberLifes,
        Kit: subscription.Plan?.Kit,
    };
}

/** Dialog de atribuição / troca de plano (e ciclo) do assinante. */
function AssignPlanDialog({ open, onOpenChange, initialData, onSaved }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const { club } = useClub();
    const { data: plans } = useFetch(open ? 'club/settings/plans' : null);
    const ctx = resolveMemberContext(initialData, club);
    const {
        isDependent,
        isReseller,
        clubHasAsaas,
        memberHasActiveAsaas: memberHasAsaas,
        isActiveInstallmentMember: isInstallmentMember,
        assignViaMoney: useMoneyChannel,
        subscriptionStatus,
        isSubscriptionCancelled,
        isSubscriptionInactive,
        currentCycle,
        currentPlanId,
    } = ctx;
    /** Cancelada: atribui pelo pacote em dinheiro (já era dinheiro ou cobrança online encerrada). */
    const cancelBlocksAssign = isSubscriptionCancelled && !useMoneyChannel;

    const planItems = useMemo(() => {
        const items = { '': 'Selecione' };
        if (Array.isArray(plans)) {
            plans.forEach(plan => {
                items[String(plan.id)] = formatPlanOptionLabel(plan, { showPrice: isReseller });
            });
        }
        return items;
    }, [plans, isReseller]);

    const form = useForm({ defaultValues: { planId: '', cycle: 'MONTHLY', plan: null, billingType: 'BOLETO' } });
    const memberBillingType = String(
        initialData?.subscription?.billingType || initialData?.subscription?.subscription?.billingType || '',
    ).toUpperCase();
    /** Recorrente no cartão: o Asaas não altera o valor sem tokenização e o painel não tem o cartão; recria no boleto. */
    const recreatesCardSubscription = isReseller && memberHasAsaas && memberBillingType === 'CREDIT_CARD';
    const { isSubmitting } = form.formState;
    const watchedPlan = form.watch('plan');
    const watchedPlanId = form.watch('planId');
    const watchedCycle = normalizeCycle(form.watch('cycle'));
    const cycleOptions = selectableCycles(watchedPlan, {
        currentCycle: initialData?.subscription ? normalizeCycle(initialData.subscription.cycle) : null,
        samePlan: Boolean(watchedPlanId) && String(watchedPlanId) === String(currentPlanId),
    });

    useEffect(() => {
        if (!open) return;
        const subscription = initialData?.subscription;
        const selectedPlan = Array.isArray(plans)
            ? plans.find(plan => String(plan.id) === currentPlanId)
            : null;
        form.reset({
            planId: currentPlanId,
            cycle: subscription
                ? normalizeCycle(subscription.cycle)
                : planCycles(selectedPlan)[0],
            plan: selectedPlan || (currentPlanId ? buildFallbackPlan(subscription) : null),
            billingType: 'BOLETO',
        });
    }, [open, form, initialData, plans, currentPlanId]);

    const hasSeguro = Boolean(
        watchedPlan?.Kit?.some(benefit => {
            const type = String(
                benefit?.Resale?.type
                    ?? benefit?.Resale?.additionalType
                    ?? benefit?.Resale?.Product?.additionalType
                    ?? benefit?.Product?.additionalType
                    ?? '',
            ).toLowerCase();
            return type === 'seguro';
        }),
    );
    const hasPermissionSeguro = !hasSeguro || initialData?.insuranceEligible !== false;
    /** Reseller: com Asaas no membro precisa chave; sem Asaas no membro = pacote Dinheiro. */
    const hasAsaasKey = isReseller ? clubHasAsaas || !memberHasAsaas : true;
    const cycleChanged = Boolean(watchedPlanId) && watchedCycle !== currentCycle;
    const showCyclePicker = isReseller && cycleOptions.length > 1 && !isDependent;
    const isFreeSelectedPlan =
        Boolean(watchedPlan?.isFree) || (Boolean(watchedPlan?.isTrial) && watchedPlan?.trialPayment === false);
    const canStartPlanChange =
        !isDependent &&
        !isInstallmentMember &&
        !cancelBlocksAssign &&
        Boolean(watchedPlanId) &&
        !(isReseller && memberHasAsaas && (Boolean(watchedPlan?.isFree) || Boolean(watchedPlan?.isTrial))) &&
        (!isReseller || isFreeSelectedPlan || useMoneyChannel || (clubHasAsaas && memberHasAsaas));
    const planChangeBlockReason = (() => {
        if (isDependent) return null;
        if (isInstallmentMember) {
            return 'Assinatura anual parcelada: a troca de plano não é feita pelo painel. O assinante escolhe o novo plano em uma nova compra, a partir de 30 dias antes do fim do ciclo.';
        }
        if (cancelBlocksAssign) {
            return 'Assinatura cancelada. Não é possível atribuir plano enquanto a cobrança online ainda estiver em andamento.';
        }
        if (!watchedPlanId || !isReseller) return null;
        if (!isFreeSelectedPlan && memberHasAsaas && !clubHasAsaas) {
            return 'Clube vendedor sem Asaas configurado. Configure a chave Asaas antes de alterar planos com cobrança online.';
        }
        if (memberHasAsaas && (Boolean(watchedPlan?.isFree) || Boolean(watchedPlan?.isTrial))) {
            return 'Quem já possui plano pago (Asaas) não pode migrar para gratuito nem para período de teste.';
        }
        return null;
    })();
    const hasCurrentPlan = Boolean(currentPlanId);
    const unchanged =
        currentPlanId === watchedPlanId && !isSubscriptionInactive && !isSubscriptionCancelled && !cycleChanged;

    const onSubmit = async values => {
        try {
            if (!hasPermissionSeguro) {
                enqueueSnackbar(
                    'Este plano inclui seguro e não pode ser contratado por quem tem 74 anos ou mais.',
                    { variant: 'error' },
                );
                return;
            }
            if (planChangeBlockReason || !canStartPlanChange) {
                enqueueSnackbar(
                    planChangeBlockReason || 'Não é possível iniciar a troca de plano com os dados atuais.',
                    { variant: 'error' },
                );
                return;
            }

            const fromPlan = Array.isArray(plans) ? plans.find(plan => String(plan.id) === currentPlanId) : null;
            const toPlan =
                values.plan ||
                (Array.isArray(plans) ? plans.find(plan => String(plan.id) === String(values.planId)) : null);
            const toCycleOptions = selectableCycles(toPlan, {
                currentCycle: initialData?.subscription ? normalizeCycle(initialData.subscription.cycle) : null,
                samePlan: String(values.planId) === String(currentPlanId),
            });
            const targetCycle = toCycleOptions.includes(normalizeCycle(values.cycle))
                ? normalizeCycle(values.cycle)
                : toCycleOptions[0];

            const fromLabel = fromPlan
                ? formatPlanOptionLabel(fromPlan, { showPrice: isReseller, cycle: currentCycle })
                : currentPlanId
                  ? `Plano #${currentPlanId}`
                  : 'Sem plano';
            const toLabel = toPlan
                ? formatPlanOptionLabel(toPlan, { showPrice: isReseller, cycle: targetCycle })
                : `Plano #${values.planId}`;
            const keepActive = subscriptionStatus === 'ACTIVE';
            const willActivateLocally =
                subscriptionStatus !== 'ACTIVE' && (!isReseller || useMoneyChannel || isFreeSelectedPlan);

            await confirm({
                title: isSubscriptionCancelled
                    ? 'Atribuir plano e reativar'
                    : hasCurrentPlan && !isSubscriptionInactive
                      ? 'Alteração de plano'
                      : 'Atribuir plano e ativar',
                description: isSubscriptionCancelled && useMoneyChannel
                    ? `De: ${fromLabel}\nPara: ${toLabel}\n\nA cobrança online cancelada encerra agora. O assinante entra no pacote em dinheiro com este plano, sem boleto nem PIX, e o acesso volta na hora.`
                    : recreatesCardSubscription
                    ? `De: ${fromLabel}\nPara: ${toLabel}\n\nA assinatura no cartão será recriada no Asaas no boleto. Nada é cobrado agora: a próxima cobrança, já com o novo valor, vence no vencimento atual e o assinante recebe o boleto. Benefícios do kit serão ajustados.`
                    : keepActive
                    ? useMoneyChannel
                        ? `De: ${fromLabel}\nPara: ${toLabel}\n\nPacote em dinheiro: a assinatura permanece ativa, sem cobrança pela plataforma. Benefícios do kit serão ajustados.`
                        : `De: ${fromLabel}\nPara: ${toLabel}\n\nA assinatura permanece ativa. Benefícios do kit serão ajustados. Se houver Asaas, valor/descrição serão atualizados sem resetar o ciclo.`
                    : willActivateLocally
                      ? useMoneyChannel
                          ? `De: ${fromLabel}\nPara: ${toLabel}\n\nPacote em dinheiro: a assinatura será ativada sem cobrança pela plataforma. Benefícios do kit serão aplicados.`
                          : `De: ${fromLabel}\nPara: ${toLabel}\n\nA assinatura será ativada com este plano. Benefícios do kit serão aplicados.`
                      : `De: ${fromLabel}\nPara: ${toLabel}\n\nA assinatura ficará aguardando confirmação do pagamento. Benefícios novos só após ativar. Se houver Asaas, valor/descrição serão atualizados sem resetar o ciclo.`,
                confirmationText:
                    isSubscriptionCancelled || !hasCurrentPlan || isSubscriptionInactive
                        ? 'Atribuir e ativar'
                        : 'Confirmar troca',
            });

            const payload = {
                planId: Number(values.planId),
                cycle: targetCycle,
                personId: initialData.id,
                ...(recreatesCardSubscription ? { billingType: values.billingType || 'BOLETO' } : {}),
            };
            const response = initialData?.subscription
                ? await server.put(`club/settings/subscription/${initialData.id}`, {
                      ...payload,
                      subscriptionId: initialData?.subscriptionId || 0,
                  })
                : await server.post('club/settings/subscription', payload);

            enqueueSnackbar(response?.data?.message || 'Plano atualizado.');
            onOpenChange(false);
            await onSaved?.();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    return (
        <Dialog open={open} onOpenChange={isOpen => !isSubmitting && onOpenChange(isOpen)}>
            <ScrollDialogContent className="sm:max-w-xl">
                <ScrollDialogHeader>
                    <DialogTitle>{hasCurrentPlan ? 'Trocar plano' : 'Atribuir plano'}</DialogTitle>
                    <p className="text-sm text-muted-foreground">{initialData?.name}</p>
                </ScrollDialogHeader>

                <DialogBody>
                    <form id="assign-plan-form" onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                        {useMoneyChannel ? (
                            <Alert>
                                <AlertTitle>Pacote em dinheiro</AlertTitle>
                                <AlertDescription>
                                    Este assinante não tem cobrança online. O plano entra como pacote em dinheiro:
                                    nenhuma cobrança é gerada pela plataforma e o recebimento fica por conta do clube.
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        {isReseller && !clubHasAsaas && memberHasAsaas ? (
                            <Alert>
                                <AlertTitle>Asaas não configurado</AlertTitle>
                                <AlertDescription>
                                    Planos com cobrança online exigem Asaas.{' '}
                                    <Link
                                        to="/clube/conta#payment-asaas"
                                        className="font-medium underline underline-offset-4"
                                    >
                                        Configurar Asaas
                                    </Link>
                                    .
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        {planChangeBlockReason ? (
                            <Alert variant="error">
                                <TriangleAlert />
                                <AlertTitle>Não é possível atribuir/trocar o plano</AlertTitle>
                                <AlertDescription>{planChangeBlockReason}</AlertDescription>
                            </Alert>
                        ) : null}

                        {!hasPermissionSeguro ? (
                            <Alert variant="destructive">
                                <AlertTitle>Atenção</AlertTitle>
                                <AlertDescription>
                                    Este plano inclui seguro e não pode ser contratado por quem tem 74 anos ou mais.
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        <Field>
                            <FieldLabel htmlFor="subscription-plan">Plano</FieldLabel>
                            <Controller
                                name="planId"
                                control={form.control}
                                render={({ field }) => (
                                    <Select
                                        items={planItems}
                                        value={field.value || ''}
                                        onValueChange={value => {
                                            field.onChange(value);
                                            const selectedPlan = Array.isArray(plans)
                                                ? plans.find(plan => String(plan.id) === value)
                                                : null;
                                            form.setValue('plan', selectedPlan || null);
                                            const options = selectableCycles(selectedPlan, {
                                                currentCycle: initialData?.subscription
                                                    ? normalizeCycle(initialData.subscription.cycle)
                                                    : null,
                                                samePlan: String(value) === String(currentPlanId),
                                            });
                                            if (!options.includes(normalizeCycle(form.getValues('cycle')))) {
                                                form.setValue('cycle', options[0]);
                                            }
                                        }}
                                        disabled={isDependent || cancelBlocksAssign}
                                    >
                                        <SelectTrigger id="subscription-plan" className="h-auto min-h-9 w-full py-2">
                                            <SelectValue placeholder="Selecione um plano" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-80">
                                            <SelectItem value="">Selecione</SelectItem>
                                            {Array.isArray(plans)
                                                ? plans.map(plan => (
                                                      <SelectItem
                                                          key={plan.id}
                                                          value={String(plan.id)}
                                                          className="items-start py-2"
                                                      >
                                                          <span className="flex flex-col gap-0.5 text-left">
                                                              <span className="font-medium leading-snug">{plan.name}</span>
                                                              <span className="text-xs font-normal text-muted-foreground">
                                                                  {[
                                                                      plan.sku ? `SKU ${plan.sku}` : null,
                                                                      isReseller ? formatPlanPrices(plan) : null,
                                                                      PLAN_VISIBILITY_LABELS[plan.visibility] ||
                                                                          plan.visibility,
                                                                      plan.isFree ? 'Gratuito' : null,
                                                                      plan.isTrial
                                                                          ? plan.trialPayment === false
                                                                              ? `Teste ${plan.trialDays || ''}d sem cobrança`
                                                                              : `Teste ${plan.trialDays || ''}d`
                                                                          : null,
                                                                      plan.status === false ? 'Inativo' : null,
                                                                  ]
                                                                      .filter(Boolean)
                                                                      .join(' · ')}
                                                              </span>
                                                              {plan.description ? (
                                                                  <span className="line-clamp-2 text-xs font-normal text-muted-foreground">
                                                                      {plan.description}
                                                                  </span>
                                                              ) : null}
                                                          </span>
                                                      </SelectItem>
                                                  ))
                                                : null}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </Field>

                        <PlanDetailsPreview plan={watchedPlan} showPrice={isReseller} />

                        {recreatesCardSubscription ? (
                            <Field>
                                <FieldLabel htmlFor="subscription-billing-type">Nova forma de pagamento</FieldLabel>
                                <Controller
                                    name="billingType"
                                    control={form.control}
                                    render={({ field }) => (
                                        <Select
                                            items={CARD_REPLACEMENT_BILLING_ITEMS}
                                            value={field.value || 'BOLETO'}
                                            onValueChange={field.onChange}
                                            disabled={cancelBlocksAssign}
                                        >
                                            <SelectTrigger id="subscription-billing-type" className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(CARD_REPLACEMENT_BILLING_ITEMS).map(([value, label]) => (
                                                    <SelectItem key={value} value={value}>
                                                        {label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                                <p className="text-xs text-muted-foreground">
                                    A assinatura é no cartão e o Asaas não permite alterar o valor dela. A troca recria a
                                    assinatura na forma escolhida; a próxima cobrança vence no vencimento atual. Para
                                    continuar no cartão, o próprio assinante troca o plano no site do clube.
                                </p>
                            </Field>
                        ) : null}

                        {showCyclePicker ? (
                            <Field>
                                <FieldLabel>Ciclo de cobrança</FieldLabel>
                                <div className="grid grid-cols-2 gap-2">
                                    {cycleOptions.map(option => {
                                        const active = watchedCycle === option;
                                        const price =
                                            option === 'YEARLY'
                                                ? planYearlyPrice(watchedPlan)
                                                : Number(watchedPlan?.price || 0);
                                        return (
                                            <button
                                                key={option}
                                                type="button"
                                                disabled={cancelBlocksAssign}
                                                onClick={() => form.setValue('cycle', option, { shouldDirty: true })}
                                                className={cn(
                                                    'flex flex-col items-start rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                                                    active ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
                                                )}
                                            >
                                                <span className={cn('font-medium', active && 'text-primary')}>
                                                    {CYCLE_LABELS[option]}
                                                </span>
                                                <span className="text-xs text-muted-foreground tabular-nums">
                                                    {formatPlanPrice(price)}/{option === 'YEARLY' ? 'ano' : 'mês'}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {memberHasAsaas && cycleChanged ? (
                                    <p className="text-xs text-muted-foreground">
                                        A troca de ciclo vale a partir do próximo vencimento da assinatura no Asaas.
                                    </p>
                                ) : null}
                            </Field>
                        ) : null}
                    </form>
                </DialogBody>

                <ScrollDialogFooter>
                    <Button type="button" variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        form="assign-plan-form"
                        disabled={
                            isSubmitting ||
                            (!hasAsaasKey && !isFreeSelectedPlan) ||
                            !hasPermissionSeguro ||
                            !watchedPlan ||
                            unchanged ||
                            !canStartPlanChange
                        }
                    >
                        {isSubmitting ? (
                            <Spinner className="size-4" />
                        ) : hasCurrentPlan && !isSubscriptionInactive ? (
                            'Confirmar troca'
                        ) : (
                            'Atribuir e ativar'
                        )}
                    </Button>
                </ScrollDialogFooter>
            </ScrollDialogContent>
        </Dialog>
    );
}

function CurrentPlanSummary({ initialData, isReseller }) {
    const subscription = initialData?.subscription;
    const plan = buildFallbackPlan(subscription);
    if (!plan || !subscription?.planId) {
        return <p className="text-sm text-muted-foreground">Nenhum plano atribuído.</p>;
    }
    const cycle = normalizeCycle(subscription.cycle);
    return (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="min-w-0">
                <p className="font-medium">{plan.name}</p>
                {plan.sku ? <p className="text-xs text-muted-foreground">SKU: {plan.sku}</p> : null}
            </div>
            {isReseller ? (
                <div className="text-right">
                    <p className="font-semibold tabular-nums">
                        {formatPlanPrice(subscription.value ?? plan.price)}
                        <span className="text-xs font-normal text-muted-foreground">
                            /{cycle === 'YEARLY' ? 'ano' : 'mês'}
                        </span>
                    </p>
                    {subscription.dueDateCycle ? (
                        <p className="text-xs text-muted-foreground">
                            {CYCLE_LABELS[cycle]} · vence em {formatDate(subscription.dueDateCycle)}
                        </p>
                    ) : null}
                    {plan.isTrial && subscription.planData?.trialDays ? (
                        <p className="text-xs text-muted-foreground">
                            Inclui {subscription.planData.trialDays} dias de teste
                        </p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export function SubscriptionTab({ mutate, initialData, onUnlinkDependent, assignOpen, onAssignOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const { club } = useClub();
    const [innerAssignOpen, setInnerAssignOpen] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [statusBusy, setStatusBusy] = useState(false);
    const [renewing, setRenewing] = useState(false);
    const [cancellingScheduled, setCancellingScheduled] = useState(false);

    const isAssignOpen = assignOpen ?? innerAssignOpen;
    const setAssignOpen = onAssignOpenChange ?? setInnerAssignOpen;

    const {
        isDependent,
        isReseller,
        hasBenefits,
        showPlanUi,
        clubHasAsaas,
        memberHasAsaas,
        isMoneyMember,
        useMoneyChannel,
        subscriptionStatus,
        isSubscriptionCancelled,
        isSubscriptionInactive,
        currentCycle,
        currentPlanId,
    } = resolveMemberContext(initialData, club);
    const holderName = initialData?.DependentBy?.name || null;
    const holderDocument = initialData?.DependentBy?.document || null;
    // Ativar/Inativar local: clube sem venda/benefit, OU reseller com membro Dinheiro.
    const manualLocalStatus = (!isReseller && !hasBenefits) || (isReseller && isMoneyMember);
    /** Pacote em dinheiro: inativar vira EXPIRED e a cancelada pode ser reativada ou receber plano. */
    const moneyPackage = isReseller && isMoneyMember;
    const blockedByCancel = isSubscriptionCancelled && !useMoneyChannel;
    /** Cancelada ou inativa: não cancela de novo. */
    const isSubscriptionTerminal = isSubscriptionCancelled || isSubscriptionInactive;
    const memberPlanIsPaid =
        Boolean(initialData?.subscription?.planId) &&
        !(initialData?.subscription?.planData?.isFree || initialData?.subscription?.Plan?.isFree);
    const canRenewMoneyCycle = moneyPackage && memberPlanIsPaid;
    const canCancelSubscription =
        Boolean(initialData?.id) &&
        Boolean(initialData?.subscription?.id || initialData?.subscriptionId) &&
        !isDependent &&
        !isSubscriptionTerminal;
    /** Cancelada sem estorno com cobrança online ainda pode receber estorno. */
    const canRefundCancelled = isSubscriptionCancelled && memberHasAsaas && !isDependent;
    const scheduledStartAt = initialData?.subscription?.scheduledStartAt || null;
    const hasScheduledPurchase = Boolean(initialData?.subscription?.scheduledPlanId) && !isDependent;
    const scheduledIsInstallment =
        String(initialData?.subscription?.scheduledBillingModel || '').toUpperCase() === 'INSTALLMENT';

    const handleCancelScheduled = async () => {
        try {
            await confirm({
                title: 'Cancelar troca agendada',
                description: scheduledIsInstallment
                    ? 'O próximo ciclo parcelado será cancelado e o valor estornado no cartão. O plano atual segue até o fim do ciclo e depois expira.'
                    : 'A próxima assinatura será removida no Asaas. O plano atual segue até o fim do ciclo e depois expira.',
                confirmationText: 'Cancelar troca',
                confirmationButtonProps: { variant: 'destructive' },
            });
            setCancellingScheduled(true);
            const { data: body } = await server.delete(`club/subscribers/${initialData.id}/subscription/scheduled`);
            enqueueSnackbar(body?.message || 'Troca agendada cancelada.');
            await mutate?.();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setCancellingScheduled(false);
        }
    };

    const handleSetLocalStatus = async nextStatus => {
        try {
            const activating = nextStatus === 'ACTIVE';
            await confirm({
                title: activating ? 'Ativar assinatura' : 'Inativar assinatura',
                description: activating
                    ? isSubscriptionCancelled
                        ? 'O cancelamento será desfeito: a assinatura volta para ACTIVE e mantém o vencimento atual.'
                        : 'A assinatura local passará para ACTIVE e o membro poderá acessar o clube.'
                    : moneyPackage
                      ? 'A assinatura passará para EXPIRED: o acesso é bloqueado e os benefícios são mantidos. Você pode ativar de novo depois.'
                      : 'A assinatura local passará para INACTIVE e o membro perderá o acesso aos benefícios.',
                confirmationText: activating ? 'Ativar' : 'Inativar',
                confirmationButtonProps: activating ? undefined : { variant: 'destructive' },
            });
            setStatusBusy(true);
            const { data: body } = await server.put(`club/subscribers/${initialData.id}/subscription/status`, {
                status: nextStatus,
            });
            enqueueSnackbar(body?.message || (activating ? 'Assinatura ativada.' : 'Assinatura inativada.'));
            await mutate?.();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setStatusBusy(false);
        }
    };

    const handleRenewMoneyCycle = async () => {
        try {
            const dueLabel = initialData?.subscription?.dueDateCycle
                ? formatDate(initialData.subscription.dueDateCycle)
                : null;
            await confirm({
                title: 'Renovar ciclo',
                description: `Confirme o recebimento em dinheiro. O ciclo ${CYCLE_LABELS[currentCycle].toLowerCase()} será renovado${
                    dueLabel ? ` a partir do vencimento atual (${dueLabel}) ou de hoje, se já venceu` : ''
                } e a assinatura ficará ativa.`,
                confirmationText: 'Renovar',
            });
            setRenewing(true);
            const { data: body } = await server.post(`club/settings/subscription/${initialData.id}/renew`);
            enqueueSnackbar(body?.message || 'Ciclo renovado.');
            await mutate?.();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setRenewing(false);
        }
    };

    const handleUnlinkDependent = async () => {
        try {
            await confirm({
                title: 'Remover vínculo de dependente',
                description:
                    'O assinante deixará de ser dependente e poderá receber um plano próprio. O titular atual não é alterado.',
                confirmationText: 'Remover vínculo',
            });
            setUnlinking(true);
            await server.put(`club/subscribers/${initialData.id}`, {
                name: initialData.name,
                email: initialData.email,
                document: String(initialData.document || '').replace(/\D/g, ''),
                phone: initialData.phone,
                birthday: formatBirthday(initialData.birthday),
                gender: initialData.gender,
                status: initialData.status,
                dependentBy: null,
            });
            enqueueSnackbar('Vínculo de dependente removido.');
            await onUnlinkDependent?.();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setUnlinking(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {isDependent ? (
                <Alert>
                    <AlertTitle>Dependente</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <span>
                            Este assinante é dependente
                            {holderName ? (
                                <>
                                    {' '}
                                    de <strong>{holderName}</strong>
                                    {holderDocument ? ` (${holderDocument})` : ''}
                                </>
                            ) : null}
                            . O plano vigente acompanha o do titular. Remova o vínculo para atribuir um plano próprio.
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-fit"
                            disabled={unlinking}
                            onClick={handleUnlinkDependent}
                        >
                            {unlinking ? <Spinner className="size-4" /> : null}
                            Remover vínculo de dependente
                        </Button>
                    </AlertDescription>
                </Alert>
            ) : null}

            <div>
                <h3 className="text-sm font-medium">Assinatura do clube</h3>
                <p className="text-sm text-muted-foreground">
                    {manualLocalStatus && !showPlanUi
                        ? 'Ative ou inative o acesso deste membro ao clube.'
                        : useMoneyChannel
                          ? 'Pacote em dinheiro: plano sem cobrança pela plataforma; controle o acesso abaixo.'
                          : 'Aqui você poderá gerenciar a assinatura deste membro.'}
                </p>
            </div>

            {showPlanUi && !isDependent ? (
                <div className="flex flex-col gap-3 rounded-lg border p-4">
                    <CurrentPlanSummary initialData={initialData} isReseller={isReseller} />
                    {isReseller && !clubHasAsaas && !memberHasAsaas ? (
                        <p className="text-xs text-muted-foreground">
                            Sem Asaas no clube, planos pagos entram pelo pacote em dinheiro. Para vender no site,
                            configure o Asaas.
                        </p>
                    ) : null}
                    <div>
                        <Button
                            type="button"
                            variant={currentPlanId ? 'outline' : 'default'}
                            disabled={blockedByCancel || hasScheduledPurchase}
                            onClick={() => setAssignOpen(true)}
                        >
                            {currentPlanId ? 'Trocar plano' : 'Atribuir plano'}
                        </Button>
                        {blockedByCancel ? (
                            <p className="mt-2 text-xs text-muted-foreground">
                                Assinatura cancelada: não é possível atribuir plano. Crie uma nova adesão ou reative
                                antes.
                            </p>
                        ) : isSubscriptionCancelled ? (
                            <p className="mt-2 text-xs text-muted-foreground">
                                Assinatura cancelada: atribuir um plano reativa a assinatura com um novo ciclo.
                            </p>
                        ) : hasScheduledPurchase ? (
                            <p className="mt-2 text-xs text-muted-foreground">
                                O próximo plano já foi contratado. Cancele a troca agendada antes de trocar o plano.
                            </p>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {manualLocalStatus && !isDependent ? (
                <div className="flex flex-col gap-3 rounded-lg border p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-muted-foreground">Status da assinatura:</span>
                        <Badge variant={subscriptionStatus === 'ACTIVE' ? 'default' : 'secondary'}>
                            {subscriptionStatus || '—'}
                        </Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {canRenewMoneyCycle ? (
                            <Button
                                type="button"
                                variant="outline"
                                disabled={renewing || statusBusy}
                                onClick={handleRenewMoneyCycle}
                            >
                                {renewing ? <Spinner className="size-4" /> : null}
                                Renovar ciclo
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            disabled={statusBusy || blockedByCancel || subscriptionStatus === 'ACTIVE'}
                            onClick={() => handleSetLocalStatus('ACTIVE')}
                        >
                            {statusBusy ? <Spinner className="size-4" /> : null}
                            Ativar assinatura
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={
                                statusBusy ||
                                isSubscriptionCancelled ||
                                subscriptionStatus === 'INACTIVE' ||
                                (moneyPackage && subscriptionStatus === 'EXPIRED')
                            }
                            onClick={() => handleSetLocalStatus('INACTIVE')}
                        >
                            {statusBusy ? <Spinner className="size-4" /> : null}
                            Inativar assinatura
                        </Button>
                    </div>
                </div>
            ) : null}

            {showPlanUi && hasScheduledPurchase ? (
                <Alert>
                    <AlertTitle>Próximo plano já contratado</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <span>
                            {scheduledIsInstallment ? 'Anual parcelado' : 'Assinatura recorrente'}
                            {scheduledStartAt ? ` a partir de ${formatDate(scheduledStartAt)}` : ''}. O plano atual segue
                            até lá.
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-fit"
                            disabled={cancellingScheduled}
                            onClick={handleCancelScheduled}
                        >
                            {cancellingScheduled ? <Spinner className="size-4" /> : null}
                            Cancelar troca agendada
                        </Button>
                    </AlertDescription>
                </Alert>
            ) : null}

            {showPlanUi && (canCancelSubscription || isSubscriptionTerminal) ? (
                <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={isSubscriptionTerminal || isDependent}
                        onClick={() => setCancelOpen(true)}
                    >
                        {isSubscriptionTerminal
                            ? subscriptionStatus === 'INACTIVE'
                                ? 'Assinatura inativa'
                                : 'Assinatura cancelada'
                            : 'Cancelar assinatura'}
                    </Button>
                    {canRefundCancelled ? (
                        <Button type="button" variant="outline" onClick={() => setCancelOpen(true)}>
                            Estornar
                        </Button>
                    ) : null}
                </div>
            ) : null}

            {showPlanUi && !isDependent && initialData?.id ? (
                <CancelSubscriptionDialog
                    open={cancelOpen}
                    onOpenChange={setCancelOpen}
                    personId={initialData.id}
                    personName={initialData.name}
                    onDone={mutate}
                />
            ) : null}

            {showPlanUi && !isDependent ? (
                <AssignPlanDialog
                    open={isAssignOpen}
                    onOpenChange={setAssignOpen}
                    initialData={initialData}
                    onSaved={mutate}
                />
            ) : null}
        </div>
    );
}

function formatAsaasSubscriptionLabel(sub) {
    const id = sub?.id || '—';
    const value = Number(sub?.value ?? 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
    const status = sub?.status || '—';
    const cycle = sub?.cycle || '';
    const nextDue = sub?.nextDueDate ? String(sub.nextDueDate).slice(0, 10) : '';
    return [id, value, status, cycle, nextDue && `próx. ${nextDue}`].filter(Boolean).join(' · ');
}

function normalizeAsaasSubscriptionId(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'object') return null;
    return String(value);
}

function toErrorText(value, fallback) {
    if (typeof value === 'string' && value.trim()) return value;
    if (Array.isArray(value)) {
        const parts = value
            .map(item => (typeof item === 'string' ? item : item?.message))
            .filter(part => typeof part === 'string' && part.trim());
        if (parts.length) return parts.join(' ');
    }
    if (value && typeof value === 'object' && typeof value.message === 'string' && value.message.trim()) {
        return value.message;
    }
    return fallback;
}

/** Tab temporária para migração legado → vincular Asaas à assinatura local. */
export function AsaasMigrationTab({ initialData, mutatePerson }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const [loadingList, setLoadingList] = useState(false);
    const [linking, setLinking] = useState(false);
    const [asaasList, setAsaasList] = useState([]);
    const [selectedId, setSelectedId] = useState(() =>
        normalizeAsaasSubscriptionId(initialData?.subscription?.subscriptionId),
    );
    const [listError, setListError] = useState('');

    const linkedAsaasId = normalizeAsaasSubscriptionId(initialData?.subscription?.subscriptionId);
    const autoLinkError = initialData?.subscription?.asaasLinkError || '';
    const documentDigits = String(initialData?.document || '').replace(/\D/g, '');

    const asaasItems = useMemo(() => {
        const items = {};
        asaasList.forEach(sub => {
            if (sub?.id == null) return;
            items[String(sub.id)] = formatAsaasSubscriptionLabel(sub);
        });
        if (selectedId && !items[selectedId]) {
            items[selectedId] = selectedId;
        }
        return Object.keys(items).length > 0 ? items : undefined;
    }, [asaasList, selectedId]);

    const loadAsaasSubscriptions = async () => {
        if (!documentDigits) return;
        setLoadingList(true);
        setListError('');
        try {
            const { data: customerBody } = await server.get('club/payment/asaas/customers', {
                params: { cpfCnpj: documentDigits },
            });
            const customerId = customerBody?.data?.id;
            if (!customerId) {
                setAsaasList([]);
                setListError('Customer Asaas não encontrado para este CPF/CNPJ.');
                return;
            }

            const { data: subsBody } = await server.get('club/payment/asaas/subscriptions', {
                params: { customer: customerId },
            });
            const raw = subsBody?.data;
            const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
            setAsaasList(list.filter(sub => sub && typeof sub === 'object' && sub.id != null));
            if (!list.length) {
                setListError('Nenhuma assinatura Asaas encontrada para este customer.');
            }
        } catch (error) {
            setAsaasList([]);
            setListError(toErrorText(error?.response?.data?.message, 'Falha ao listar assinaturas Asaas.'));
            if (error?.response?.status && error.response.status !== 404) {
                setErrors(error);
            }
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        setSelectedId(normalizeAsaasSubscriptionId(initialData?.subscription?.subscriptionId));
    }, [initialData?.subscription?.subscriptionId]);

    useEffect(() => {
        loadAsaasSubscriptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- carrega ao abrir o assinante
    }, [initialData?.id, documentDigits]);

    const handleLink = async () => {
        if (!selectedId || !initialData?.id) return;
        try {
            await confirm({
                title: 'Vincular assinatura Asaas',
                description: `Vincular ${selectedId} à assinatura local deste assinante? O histórico de faturas será importado e o vencimento do ciclo passa a contar da fatura paga mais recente.`,
                confirmationText: 'Vincular',
            });
            setLinking(true);
            const { data: linkBody } = await server.put(
                `club/subscribers/${initialData.id}/subscription/link-asaas`,
                { subscriptionAsaasId: selectedId },
            );
            enqueueSnackbar(linkBody?.message || 'Assinatura Asaas vinculada.');
            await mutatePerson?.();
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        } finally {
            setLinking(false);
        }
    };

    const selectedInList = asaasList.some(sub => String(sub?.id) === selectedId);

    return (
        <div className="flex flex-col gap-4">
            <Alert>
                <AlertTitle>Migração (temporário)</AlertTitle>
                <AlertDescription>
                    Liste assinaturas Asaas do CPF/CNPJ e vincule à assinatura local. Use em clubes migrados do legado.
                </AlertDescription>
            </Alert>

            {autoLinkError ? (
                <Alert variant="destructive">
                    <AlertTitle>Vínculo automático não concluído</AlertTitle>
                    <AlertDescription>{autoLinkError}</AlertDescription>
                </Alert>
            ) : null}

            <div className="rounded-lg border px-3 py-2 text-sm">
                <p className="text-muted-foreground">Vínculo Asaas atual</p>
                <p className="font-medium tabular-nums">{linkedAsaasId || 'Nenhuma assinatura Asaas vinculada'}</p>
            </div>

            <Field>
                <FieldLabel htmlFor="asaas-subscription">Assinaturas Asaas</FieldLabel>
                <Select
                    items={asaasItems}
                    value={selectedId}
                    onValueChange={value => setSelectedId(normalizeAsaasSubscriptionId(value))}
                    disabled={loadingList}
                >
                    <SelectTrigger id="asaas-subscription" className="w-full">
                        <SelectValue
                            placeholder={loadingList ? 'Carregando…' : 'Selecione uma assinatura Asaas'}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        {asaasList.map(sub => (
                            <SelectItem key={String(sub.id)} value={String(sub.id)}>
                                {formatAsaasSubscriptionLabel(sub)}
                            </SelectItem>
                        ))}
                        {selectedId && !selectedInList ? (
                            <SelectItem value={selectedId}>{selectedId}</SelectItem>
                        ) : null}
                    </SelectContent>
                </Select>
                {listError ? <p className="mt-1 text-sm text-destructive">{listError}</p> : null}
            </Field>

            <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
                <Button type="button" variant="outline" disabled={loadingList} onClick={loadAsaasSubscriptions}>
                    {loadingList ? <Spinner className="size-4" /> : null}
                    Atualizar lista
                </Button>
                <Button
                    type="button"
                    disabled={linking || loadingList || !selectedId || selectedId === linkedAsaasId}
                    onClick={handleLink}
                >
                    {linking ? <Spinner className="size-4" /> : null}
                    Vincular à assinatura local
                </Button>
            </div>
        </div>
    );
}
